<template>
	<v-card
		width="100%"
		height="100%"
		:class="[isRestrictedCore && 'card-disabled', userflowClass]"
		variant="outlined"
		data-test="core-card"
	>
		<v-card-text class="resource-card-shell entity-card">
			<div class="entity-card__body">
				<!-- Icon + Title -->
				<div class="entity-card__header">
					<div class="sgnl-flex sgnl-justify-space-between sgnl-align-center">
						<img
							:src="coreIcon"
							width="32"
							height="32"
							alt="Core"
						/>
						<template v-if="isEditableLocalCore">
							<TooltipButton
								v-if="syncError"
								:icon="true"
								@click="syncCoreWithCloud"
								tooltipText="An error occurred while synchronising. Click to try again."
							>
								<template #btnContent>
									<CloudAlerting16 class="sgnl-icon-color-error" />
								</template>
							</TooltipButton>
							<v-progress-circular
								v-else-if="syncingCore"
								:indeterminate="true"
								color="warning"
								size="16"
							/>
							<TooltipButton
								v-else
								:icon="true"
								@click="syncCoreWithCloud"
								tooltipText="Core not synced with Signaloid Cloud. Click to synchronise."
							>
								<template #btnContent>
									<Renew16 class="sgnl-icon-color-warning" />
								</template>
							</TooltipButton>
						</template>
					</div>
					<div class="sgnl-flex sgnl-justify-space-between sgnl-align-center">
						<div>
							<v-tooltip
								bottom
								:allow-overflow="true"
							>
								<template v-slot:activator="{ props }">
									<span
										v-bind="props"
										class="sgnl-paragraph-m-medium line-clamp-1 sgnl-text-color-default"
										data-test="core-name-label"
									>
										{{ coreConfig.Name }}
									</span>
								</template>
								<div>
									<template v-if="coreConfig.Class">
										Core Class: {{ coreConfig.Class }}<br />
									</template>
									<template v-if="coreConfig.MemorySize">
										Memory Allocation: {{ coreMemorySizeString }}<br />
									</template>
									<template
										v-if="coreConfig.Microarchitecture !== 'Reference' && coreConfig.Precision"
									>
										Signaloid Representation Size: {{ coreConfig.Precision }}<br />
									</template>
									<template v-if="coreConfig.Microarchitecture">
										Microachitecture: {{ coreConfig.Microarchitecture }}<br />
									</template>
									<template v-if="correlationTrackingMethodPrettyName">
										Correlation Tracking: {{ correlationTrackingMethodPrettyName }} <br />
									</template>
								</div>
							</v-tooltip>
						</div>
					</div>
				</div>

				<div class="entity-card__meta">
					<!-- Core ID -->
					<div :class="{ 'core-id-row-disabled': isRestrictedCore }">
						<v-chip
							variant="outlined"
							class="badge-muted copy-id-chip"
							data-test="task-card-task-id-value"
							:disabled="isRestrictedCore"
						>
							Core ID: <span class="truncated">{{ coreConfig.CoreID }}</span>
							<CopyToClipboardButton
								class="core-id-copy-btn"
								:value-to-copy="coreConfig.CoreID"
								value-name="Core ID"
								:disabled="isRestrictedCore"
							/>
						</v-chip>
					</div>

					<!-- Core chips -->
					<div class="sgnl-flex sgnl-flex-wrap info-badges">
						<v-chip
							variant="text"
							:disabled="isRestrictedCore"
						>
							<Chip16 />
							{{ coreMicroarchitectureStringFormat }}
						</v-chip>
						<v-chip
							variant="text"
							:disabled="isRestrictedCore"
						>
							<Carbon16 />
							{{ coreConfig.Class }}
						</v-chip>
						<v-chip
							variant="text"
							:disabled="isRestrictedCore"
						>
							<Save16 />
							{{ coreMemorySizeString }}
						</v-chip>
						<v-chip
							v-if="coreConfig.Microarchitecture !== 'Reference'"
							variant="text"
							class="badge-colored"
							:style="{
								'--badge-color': correlationTrackingChipStyle.cssColor,
								'--badge-border-color': correlationTrackingChipStyle.cssBorderColor,
							}"
							:disabled="isRestrictedCore"
						>
							<component :is="correlationTrackingChipStyle.icon" />
							{{ correlationTrackingChipStyle.text }}
						</v-chip>
						<v-chip
							v-if="!isSupportedCore"
							variant="text"
							class="badge-colored badge-colored--error"
						>
							<SubtractFilled16 class="sgnl-icon-color-error" />
							Deprecated
						</v-chip>
					</div>
				</div>
			</div>

			<div class="entity-card__actions">
				<UpgradeButton
					v-if="isRestrictedCore && isSupportedCore"
					size="default"
				/>
				<template v-if="coreConfig.Owner !== '\*'">
					<!-- Edit button -->
					<v-btn
						v-if="isSupportedCore"
						@click="editCoreRequest"
						variant="flat"
						color="primary"
						data-test="edit-core-button"
					>
						<Edit16 />
						<span>Edit</span>
					</v-btn>
					<v-btn
						@click="deleteCoreRequest"
						variant="outlined"
						color="error"
						data-test="delete-core-button"
					>
						<TrashCan16 class="sgnl-icon-color-error" />
						<span>Delete</span>
					</v-btn>
				</template>

				<!-- Details button -->
				<v-btn
					v-else
					@click="editCoreRequest"
					variant="outlined"
					data-test="details-core-button"
				>
					<DataViewAlt16 />
					<span>Details</span>
				</v-btn>
			</div>
		</v-card-text>
	</v-card>
</template>

<script lang="ts">
import { defineComponent, markRaw, PropType } from "vue";
import { toStringWithByteMagnitude, shortenID } from "@/js/utilities";

import TooltipButton from "@/components/Common/TooltipButton.vue";
import UpgradeButton from "@/components/Common/UpgradeButton.vue";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton.vue";
import { sdk } from "@/js/sdk";
import { useCoresStore } from "@/stores/cores";

import { Core, CoreCorrelationType, CoreCorrelationTypeCodeE } from "@/types/api/cores";
import type { CoreClass, Microarchitecture, CorrelationTracking } from "@signaloid/cloud-compute-engine-client";
import { monitoringCaptureError } from "@/plugins/monitoring";

import coreProIcon from "@/assets/icons/cores/core-pro.svg";
import coreDefaultIcon from "@/assets/icons/cores/core-default.svg";

import Edit16 from "@carbon/icons-vue/es/edit/16";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";
import Chip16 from "@carbon/icons-vue/es/chip/16";
import SubtractFilled16 from "@carbon/icons-vue/es/subtract--filled/16";
import CloudAlerting16 from "@carbon/icons-vue/es/cloud--alerting/16";
import Renew16 from "@carbon/icons-vue/es/renew/16";
import DataViewAlt16 from "@carbon/icons-vue/es/data--view--alt/16";
import Restart16 from "@carbon/icons-vue/es/restart/16";
import Carbon16 from "@carbon/icons-vue/es/carbon/16";
import Save16 from "@carbon/icons-vue/es/save/16";

type ComponentData = {
	syncError: boolean;
	syncingCore: boolean;
	showDetails: boolean;
};
export default defineComponent({
	name: "CoreCard",
	components: {
		TooltipButton,
		UpgradeButton,
		CopyToClipboardButton,
		Edit16,
		TrashCan16,
		Chip16,
		SubtractFilled16,
		CloudAlerting16,
		Renew16,
		Carbon16,
		Save16,
		DataViewAlt16,
	},
	emits: ["edit-core", "delete-core", "core-synced"],
	data: (): ComponentData => ({
		syncError: false,
		syncingCore: false,
		showDetails: false,
	}),
	props: { coreConfig: { type: Object as PropType<Core>, required: true } },
	setup() {
		const coresStore = useCoresStore();
		return { coresStore, shortenID };
	},
	methods: {
		editCoreRequest() {
			this.$emit("edit-core", this.coreConfig);
		},
		deleteCoreRequest() {
			this.$emit("delete-core", this.coreConfig);
		},
		async syncCoreWithCloud() {
			this.syncingCore = true;
			// console.log("(Syncing core with cloud) sync request :>> ", this.coreConfig);

			try {
				await sdk.cores.create({
					Name: this.coreConfig.Name,
					Class: this.coreConfig.Class as CoreClass,
					Precision: this.coreConfig.Precision!,
					MemorySize: this.coreConfig.MemorySize,
					Microarchitecture: this.coreConfig.Microarchitecture as Microarchitecture,
					CorrelationTracking: this.coreConfig.CorrelationTracking as CorrelationTracking,
				});
				// console.log("(Syncing core with cloud) response :>> ", resp);
				this.$emit("core-synced", this.coreConfig);
				// console.log("(Syncing core with cloud) removing local core :>> ", this.coreConfig.CoreID);
				this.coresStore.removeCoreFromLocalCoresList(this.coreConfig);
			} catch (error) {
				monitoringCaptureError(error, "Sync core with cloud");
				this.syncError = true;
			} finally {
				this.syncingCore = false;
			}
		},
	},
	computed: {
		isEditableLocalCore(): boolean {
			return !this.coreConfig.Owner && !this.coreConfig?.CoreID?.includes("cor_");
		},
		isRestrictedCore(): boolean {
			return this.coreConfig.Disabled ? this.coreConfig.Disabled : false;
		},
		isSupportedCore(): boolean {
			return !this.coreConfig.Deprecated;
		},
		correlationTrackingMethodPrettyName(): null | CoreCorrelationType {
			const supportedCorelationTrackingMethods = this.coresStore
				.correlationTrackingSupportedByCore(this.coreConfig.Microarchitecture)
				.find((m) => {
					return m.Code == this.coreConfig.CorrelationTracking;
				});
			if (!supportedCorelationTrackingMethods) {
				return null;
			}
			return supportedCorelationTrackingMethods.Name;
		},
		coreMemorySizeString(): string {
			return toStringWithByteMagnitude(this.coreConfig.MemorySize);
		},
		coreMicroarchitectureStringFormat(): string {
			return this.coresStore.coreMicroarchitectureStringFormat(
				this.coreConfig.Microarchitecture,
				this.coreConfig.Precision,
			);
		},
		correlationTrackingChipStyle(): {
			color: string;
			cssColor: string;
			cssBorderColor: string;
			icon: any;
			text: string;
		} {
			const chipStyle: { color: string; cssColor: string; cssBorderColor: string; icon: any; text: string } = {
				color: "inactive",
				cssColor: "var(--sgnl-default-secondary-text)",
				cssBorderColor: "var(--sgnl-default-primary-border)",
				icon: markRaw(Restart16),
				text: "Off",
			};

			if (!this.coreConfig.CorrelationTracking) {
				return chipStyle;
			}

			const isEnabled = this.coreConfig.CorrelationTracking !== CoreCorrelationTypeCodeE.Disabled;
			chipStyle.color = isEnabled ? "accent" : "inactive";
			chipStyle.cssColor = isEnabled ? "var(--sgnl-brand-text)" : "var(--sgnl-default-secondary-text)";
			chipStyle.cssBorderColor = isEnabled ? "var(--sgnl-brand-border)" : "var(--sgnl-default-primary-border)";
			chipStyle.icon = isEnabled ? markRaw(Restart16) : markRaw(Restart16);
			chipStyle.text = isEnabled ? "Autocorrelation On" : "Autocorrelation Off";

			return chipStyle;
		},
		userflowClass(): string {
			return `userflow-core-${this.coreConfig.Name.toLowerCase()
				.replace(/\+/g, "plus")
				.replace(/[^a-z0-9]/g, "-")}`;
		},
		coreIcon(): string {
			if (this.coreConfig.Class === "C0Pro") {
				return coreProIcon;
			} else {
				return coreDefaultIcon;
			}
		},
	},
});
</script>

<style lang="scss">
@media (min-width: 960px) {
	.core-id-copy-btn .v-btn {
		width: 24px;
		height: 24px;
		min-width: 24px;
	}
}

.card-disabled {
	background-color: var(--sgnl-default-secondary-background-mono);
}
.core-id-row-disabled {
	cursor: not-allowed;
}
.truncated {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 120px;
}
</style>
